import { useState, useRef } from 'react';
import {
  Clock, TrendingUp, TrendingDown, Bookmark,
  BookOpen, Loader2, CheckCircle, X,
} from 'lucide-react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import type { SportRecommendation } from '../../types';
import { useAppStore } from '../../store/useAppStore';
import { TeamLogo } from '../common/TeamLogo';

const DECISION_STYLES: Record<string, { label: string; className: string }> = {
  STRONG_BET:    { label: 'Strong Bet',    className: 'bg-win/20 text-win border-win/30'             },
  QUALIFIED_BET: { label: 'Qualified Bet', className: 'bg-brand/20 text-brand border-brand/30'       },
  LEAN:          { label: 'Lean',          className: 'bg-warn/20 text-warn border-warn/30'           },
  NO_BET:        { label: 'No Bet',        className: 'bg-surface-3 text-gray-500 border-surface-4'  },
  INVALID:       { label: 'Invalid',       className: 'bg-loss/20 text-loss border-loss/30'           },
};

const TIMING_STYLES: Record<string, { label: string; className: string }> = {
  BET_NOW: { label: 'Bet Now',  className: 'text-gold-bright' },
  WAIT:    { label: 'Wait',     className: 'text-warn'         },
  MONITOR: { label: 'Monitor',  className: 'text-brand'        },
  AVOID:   { label: 'Avoid',    className: 'text-loss'         },
};

const LONG_PRESS_MS = 500;

// ── Inline Bet Slip ────────────────────────────────────────────────────────────

function InlineBetSlip({ rec, onClose }: { rec: SportRecommendation; onClose: () => void }) {
  const { placeBet, bankrollSettings } = useAppStore();
  const suggestedStake = bankrollSettings?.unitSize ?? 25;
  const [stake, setStake] = useState(String(Math.round(suggestedStake)));
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const stakeNum = parseFloat(stake) || 0;
  const payout = rec.odds >= 0
    ? stakeNum * (rec.odds / 100)
    : stakeNum * (100 / Math.abs(rec.odds));
  const totalReturn = stakeNum + payout;

  async function handleConfirm() {
    if (stakeNum <= 0) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      await placeBet({
        gamePk: rec.eventId,
        eventPk: String(rec.eventId),
        gameDate: rec.scheduledDate,
        leagueId: rec.leagueId,
        awayTeam: rec.awayTeamName,
        homeTeam: rec.homeTeamName,
        market: rec.market,
        selection: rec.selection,
        odds: rec.odds,
        stakeAmount: stakeNum,
        modelProbability: rec.modelProbability,
        edge: rec.edge,
        confidence: rec.confidence,
        recommendationId: rec.id,
      });
      setStatus('success');
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to place bet');
    }
  }

  if (status === 'success') {
    return (
      <div className="flex items-center gap-2 mt-2 bg-win/10 border border-win/30 rounded-lg px-3 py-2 text-xs text-win font-semibold">
        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
        Bet logged · ${stakeNum} on {rec.selection}
      </div>
    );
  }

  return (
    <div className="mt-2 bg-surface-3 border border-brand/25 rounded-lg p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-brand uppercase tracking-widest">Log Bet</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-400">
            {rec.selection} ·{' '}
            <span className={clsx('font-bold', rec.odds > 0 ? 'text-win' : 'text-gray-200')}>
              {rec.odds > 0 ? '+' : ''}{rec.odds}
            </span>
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="text-gray-600 hover:text-gray-300 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Stake input */}
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none">$</span>
          <input
            type="number"
            min={1}
            step={5}
            value={stake}
            onChange={(e) => setStake(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-surface-0 border border-surface-4 rounded pl-5 pr-2 py-1.5 text-sm font-mono text-gray-100 focus:outline-none focus:border-brand/50 focus:ring-1 focus:ring-brand/20"
            autoFocus
          />
        </div>
        {[10, 25, 50, 100].map((v) => (
          <button
            key={v}
            onClick={(e) => { e.stopPropagation(); setStake(String(v)); }}
            className={clsx(
              'text-[10px] px-1.5 py-1 rounded border transition-colors flex-shrink-0',
              stakeNum === v
                ? 'border-brand/50 bg-brand/15 text-brand'
                : 'border-surface-4 text-gray-600 hover:text-gray-300',
            )}
          >
            ${v}
          </button>
        ))}
      </div>

      {/* Payout */}
      <div className="flex items-center gap-3 text-[10px] bg-surface-0 rounded px-2 py-1.5">
        <span className="text-gray-600">Win</span>
        <span className="font-mono text-win font-bold">${payout.toFixed(2)}</span>
        <span className="text-gray-700 mx-1">·</span>
        <span className="text-gray-600">Return</span>
        <span className="font-mono text-gray-300">${totalReturn.toFixed(2)}</span>
      </div>

      {status === 'error' && (
        <p className="text-xs text-loss">{errorMsg || 'Failed. Try again.'}</p>
      )}

      <div className="flex gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); void handleConfirm(); }}
          disabled={stakeNum <= 0 || status === 'loading'}
          className={clsx(
            'flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold transition-all',
            stakeNum > 0 && status !== 'loading'
              ? 'bg-win text-surface-0 hover:brightness-110'
              : 'bg-surface-4 text-gray-600 cursor-not-allowed',
          )}
        >
          {status === 'loading'
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : `Confirm · $${stakeNum || 0}`}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="px-3 py-1.5 rounded text-xs text-gray-500 hover:text-gray-200 border border-surface-4 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── OpportunityCard ───────────────────────────────────────────────────────────

interface OpportunityCardProps {
  rec: SportRecommendation;
}

export function OpportunityCard({ rec }: OpportunityCardProps) {
  const { openWorkspaceDrawer, openActionSheet, isWatched } = useAppStore();
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const [betSlipOpen, setBetSlipOpen] = useState(false);

  const isBetNow = rec.timing === 'BET_NOW';
  const isStrong = rec.decisionStatus === 'STRONG_BET';
  const decision = DECISION_STYLES[rec.decisionStatus] ?? DECISION_STYLES.NO_BET;
  const timing = TIMING_STYLES[rec.timing] ?? TIMING_STYLES.MONITOR;
  const confidence = rec.confidence ?? 0;
  const edge = rec.edge ?? 0;
  const watched = isWatched(rec.eventId);

  // Derived stats
  const impliedProb = rec.odds >= 0
    ? 100 / (rec.odds + 100)
    : Math.abs(rec.odds) / (Math.abs(rec.odds) + 100);
  const modelProb = impliedProb + edge / 100;

  const confColor = confidence >= 0.7 ? 'bg-win' : confidence >= 0.55 ? 'bg-warn' : 'bg-gray-600';
  const confTextColor = confidence >= 0.7 ? 'text-win' : confidence >= 0.55 ? 'text-warn' : 'text-gray-400';
  const edgeColor = edge >= 3 ? 'text-win' : edge >= 1 ? 'text-warn' : 'text-gray-400';

  // ── Long-press (mobile) ───────────────────────────────────────────────────
  function onTouchStart() {
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      openActionSheet(rec);
    }, LONG_PRESS_MS);
  }

  function onTouchEnd() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function onTouchMove() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handleCardClick() {
    if (didLongPress.current || betSlipOpen) return;
    openWorkspaceDrawer(rec);
  }

  const showExpansion = isHovered || betSlipOpen;

  return (
    <div
      className={clsx(
        'group relative rounded-lg border transition-all duration-150',
        isBetNow
          ? 'border-gold/30 bg-gold/[0.04] hover:border-gold/50 hover:bg-gold/[0.07]'
          : isStrong
            ? 'border-win/20 bg-surface-1 hover:border-win/30 hover:bg-surface-2/40'
            : 'border-surface-4 bg-surface-1 hover:border-surface-3 hover:bg-surface-2/25',
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { if (!betSlipOpen) setIsHovered(false); }}
    >
      {/* BET NOW animated top accent */}
      {isBetNow && (
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-70" />
      )}

      {/* ── Base card (clickable) ─────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={handleCardClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
        className="w-full text-left px-3 py-2.5 active:scale-[0.99] transition-transform duration-75"
      >
        <div className="flex items-start gap-2.5">
          {/* League badge */}
          <span className={clsx(
            'px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex-shrink-0 mt-0.5 border',
            rec.leagueId === 'mlb'
              ? 'bg-brand/15 text-brand border-brand/25'
              : rec.leagueId === 'wnba'
                ? 'bg-warn/15 text-warn border-warn/25'
                : 'bg-surface-3 text-gray-400 border-surface-4',
          )}>
            {rec.leagueId.toUpperCase()}
          </span>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                  <TeamLogo abbrev={rec.awayTeamAbbrev} name={rec.awayTeamName} leagueId={rec.leagueId} size="xs" />
                  <span className="text-[13px] font-semibold text-gray-100 truncate leading-tight min-w-0">
                    {rec.awayTeamName}
                  </span>
                  <span className="text-[11px] text-gray-600 flex-shrink-0">@</span>
                  <TeamLogo abbrev={rec.homeTeamAbbrev} name={rec.homeTeamName} leagueId={rec.leagueId} size="xs" />
                  <span className="text-[13px] font-semibold text-gray-100 truncate leading-tight min-w-0">
                    {rec.homeTeamName}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                  {rec.market} · <span className="text-gray-300">{rec.selection}</span>
                  {' · '}
                  <span className={clsx('font-mono font-semibold', rec.odds > 0 ? 'text-win' : 'text-gray-300')}>
                    {rec.odds > 0 ? '+' : ''}{rec.odds}
                  </span>
                </p>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                {watched && <Bookmark className="w-3 h-3 text-warn fill-warn" />}
                <span className={clsx('px-1.5 py-0.5 rounded border text-[10px] font-semibold whitespace-nowrap', decision.className)}>
                  {decision.label}
                </span>
              </div>
            </div>

            {/* Metrics row */}
            <div className="flex items-center gap-3 mt-1.5">
              {/* Confidence */}
              <div className="flex items-center gap-1.5">
                <div className="w-14 h-1 bg-surface-3 rounded-full overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all duration-500', confColor)}
                    style={{ width: `${Math.round(confidence * 100)}%` }}
                  />
                </div>
                <span className={clsx('text-[11px] font-mono font-semibold tabular-nums', confTextColor)}>
                  {Math.round(confidence * 100)}%
                </span>
              </div>

              {/* Edge */}
              <div className="flex items-center gap-1">
                {edge >= 0
                  ? <TrendingUp className="w-3 h-3 text-win" />
                  : <TrendingDown className="w-3 h-3 text-loss" />}
                <span className={clsx('text-[11px] font-mono font-semibold tabular-nums', edgeColor)}>
                  {edge >= 0 ? '+' : ''}{edge.toFixed(1)}%
                </span>
              </div>

              {/* Timing */}
              <div className="flex items-center gap-1 ml-auto">
                <Clock className="w-3 h-3 text-gray-600 flex-shrink-0" />
                <span className={clsx('text-[10px] font-semibold', timing.className)}>
                  {timing.label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </button>

      {/* ── Hover expansion ──────────────────────────────────────────────────── */}
      <AnimatePresence initial={false}>
        {showExpansion && (
          <motion.div
            key="expansion"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-2.5 border-t border-surface-4/60">
              <AnimatePresence mode="wait">
                {!betSlipOpen ? (
                  <motion.div
                    key="intel"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                  >
                    {/* Intelligence metrics */}
                    <div className="flex items-center gap-3 pt-2 pb-2.5">
                      <div className="flex items-center gap-1.5 text-[10px]">
                        <span className="text-gray-600 uppercase tracking-wide">Implied</span>
                        <span className="font-mono text-gray-400">{(impliedProb * 100).toFixed(1)}%</span>
                      </div>
                      <div className="w-px h-3 bg-surface-4 flex-shrink-0" />
                      <div className="flex items-center gap-1.5 text-[10px]">
                        <span className="text-gray-600 uppercase tracking-wide">Model</span>
                        <span className="font-mono text-win">~{(Math.min(modelProb, 0.99) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="w-px h-3 bg-surface-4 flex-shrink-0" />
                      <div className="flex items-center gap-1.5 text-[10px] ml-auto">
                        <span className="text-gray-600 uppercase tracking-wide">Date</span>
                        <span className="font-mono text-gray-400">
                          {new Date(rec.scheduledDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); openWorkspaceDrawer(rec); }}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-surface-2 border border-surface-4 text-gray-300 hover:text-gray-100 hover:border-surface-3 transition-colors"
                      >
                        <BookOpen className="w-3 h-3" />
                        View Intelligence
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setBetSlipOpen(true); }}
                        className={clsx(
                          'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-colors',
                          isBetNow
                            ? 'bg-gold/15 border border-gold/30 text-gold-bright hover:bg-gold/25'
                            : 'bg-win/10 border border-win/20 text-win hover:bg-win/20',
                        )}
                      >
                        <TrendingUp className="w-3 h-3" />
                        Log Bet
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="betslip"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                  >
                    <InlineBetSlip rec={rec} onClose={() => { setBetSlipOpen(false); setIsHovered(false); }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
